/**
 * Create Opportunity Page - REDESIGNED
 * Layout mirrors EditOpportunityPage with creation logic
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import Navbar from '../components/Navbar';
import '../styles/colors.css';

const LOOKING_FOR_OPTIONS = ['musician', 'band', 'venue', 'photographer', 'vendor', 'studio'];

// Preview auto-delete date based on deletion timeframe and opportunity end
const previewAutoDeleteAt = (deletionTimeframe, opportunityEnd) => {
  const now = new Date();
  const durationDeadline = new Date(now);
  
  if (deletionTimeframe === '1week') {
    durationDeadline.setDate(durationDeadline.getDate() + 7);
  } else if (deletionTimeframe === '2weeks') {
    durationDeadline.setDate(durationDeadline.getDate() + 14);
  } else if (deletionTimeframe === '1month') {
    durationDeadline.setMonth(durationDeadline.getMonth() + 1);
  } else {
    return null;
  }

  let endDeadline = null;
  if (opportunityEnd) {
    endDeadline = new Date(opportunityEnd);
    endDeadline.setDate(endDeadline.getDate() + 1);
  }

  const autoDeleteDate = endDeadline && endDeadline < durationDeadline ? endDeadline : durationDeadline;
  return autoDeleteDate.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
  });
};

const CreateOpportunityPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    city: '',
    state: '',
    travel_distance: '',
    compensation: 'tbd',
    compensation_amount: '',
    looking_for: [],
    contact_info: '',
    opportunity_start: '',
    opportunity_end: '',
    deletion_timeframe: ''
  });

  const [collaborators, setCollaborators] = useState([]);
  const [showAddCollab, setShowAddCollab] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await apiClient.get(`/search/users?q=${encodeURIComponent(searchQuery)}`);
        if (response.data.success) {
          setSearchResults(response.data.users);
        }
      } catch (err) {
        console.error('Search failed:', err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLookingForToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      looking_for: prev.looking_for.includes(type)
        ? prev.looking_for.filter(t => t !== type)
        : [...prev.looking_for, type]
    }));
  };

  const handleAddCollaborator = (result) => {
    const newCollab = {
      name: result.display_name,
      role: result.account_role,
      id: result.id
    };
    setCollaborators(prev => [...prev, newCollab]);
    setSearchQuery('');
    setSearchResults([]);
    setShowAddCollab(false);
  };

  const handleRemoveCollaborator = (index) => {
    setCollaborators(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.deletion_timeframe) {
      setError('Please select how long this post should stay active.');
      return;
    }

    setLoading(true);
    try {
      const oppResponse = await apiClient.post('/opportunities', {
        ...formData,
        travel_distance: parseInt(formData.travel_distance)
      });

      if (oppResponse.data.success) {
        const oppId = oppResponse.data.opportunity.id;

        for (const collab of collaborators) {
          try {
            await apiClient.post(`/opportunities/${oppId}/collaborators`, {
              collaborator_name: collab.name,
              collaborator_role: collab.role,
              collaborator_id: collab.id || null
            });
          } catch (err) {
            console.error('Failed to add collaborator', err);
          }
        }

        alert('Opportunity created successfully!');
        navigate(`/opportunities/${oppId}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create opportunity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .create-opportunity-input {
            font-size: 16px !important;
          }

          .create-opportunity-select {
            font-size: 16px !important;
          }

          .create-opportunity-textarea {
            font-size: 16px !important;
          }

          .create-opportunity-title-input {
            font-size: 24px !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .create-opportunity-two-column {
            display: flex !important;
            flex-direction: column !important;
          }

          .create-opportunity-description-section {
            order: 2 !important;
          }

          .create-opportunity-info-section {
            order: 1 !important;
          }

          .create-opportunity-looking-for-grid {
            grid-template-columns: 1fr 1fr !important;
          }

          .create-opportunity-checkbox {
            width: 20px !important;
            height: 20px !important;
          }

          .create-opportunity-checkbox-label {
            font-size: 16px !important;
          }

          .create-opportunity-header {
            flex-direction: row !important;
            justify-content: space-between !important;
            align-items: center !important;
            gap: 16px !important;
          }

          .create-opportunity-header h1 {
            font-size: 24px !important;
            text-align: center !important;
            flex: 1 !important;
          }

          .create-opportunity-header-spacer {
            width: 80px !important;
          }
        }
      `}</style>
      <Navbar />
      <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', paddingTop: '140px', paddingBottom: '60px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 20px' }}>

          <div className="create-opportunity-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <button onClick={() => navigate('/opportunities')} className="btn btn-secondary">Cancel</button>
            <h1 style={{ color: 'var(--text-light)', fontSize: '32px', fontWeight: '700', margin: 0 }}>Create Opportunity</h1>
            <div className="create-opportunity-header-spacer" style={{ width: '100px' }}></div>
          </div>

          {error && (
            <div style={{ background: 'var(--accent-danger)', color: 'var(--text-light)', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            
            <div style={{ marginBottom: '24px' }}>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Opportunity Title"
                className="create-opportunity-title-input"
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '32px',
                  fontWeight: '700',
                  background: 'var(--bg-card)',
                  color: 'var(--text-dark)',
                  border: '2px solid var(--border-light)',
                  borderRadius: '8px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-light)'}
              />
            </div>

            <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <label style={{ display: 'block', marginBottom: '16px', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>
                Looking For * (Select all that apply)
              </label>
              <div className="create-opportunity-looking-for-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {LOOKING_FOR_OPTIONS.map(type => (
                  <label key={type} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.looking_for.includes(type)}
                      onChange={() => handleLookingForToggle(type)}
                      className="create-opportunity-checkbox"
                      style={{ marginRight: '8px', width: '18px', height: '18px' }}
                    />
                    <span className="create-opportunity-checkbox-label" style={{ fontSize: '14px', color: 'var(--text-dark)', textTransform: 'capitalize' }}>{type}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="create-opportunity-two-column" style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '32px', marginBottom: '32px' }}>
              
              <div className="create-opportunity-info-section" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', boxShadow: 'var(--shadow-card)' }}>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>City *</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} required className="create-opportunity-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }} />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>State *</label>
                  <input type="text" name="state" value={formData.state} onChange={handleChange} required className="create-opportunity-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }} />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Travel Distance (miles)</label>
                  <input type="number" name="travel_distance" value={formData.travel_distance} onChange={handleChange} min="0" placeholder="0" className="create-opportunity-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }} />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Start Date *</label>
                  <input type="date" name="opportunity_start" value={formData.opportunity_start} onChange={handleChange} required className="create-opportunity-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }} />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>End Date</label>
                  <input type="date" name="opportunity_end" value={formData.opportunity_end} onChange={handleChange} className="create-opportunity-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }} />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Post Duration *</label>
                  <select name="deletion_timeframe" value={formData.deletion_timeframe} onChange={handleChange} required className="create-opportunity-select" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}>
                    <option value="">Select duration...</option>
                    <option value="1week">1 Week</option>
                    <option value="2weeks">2 Weeks</option>
                    <option value="1month">1 Month</option>
                  </select>
                  {formData.deletion_timeframe && (
                    <p style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      Auto-deletes: {previewAutoDeleteAt(formData.deletion_timeframe, formData.opportunity_end)}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Compensation</label>
                  <select name="compensation" value={formData.compensation} onChange={handleChange} className="create-opportunity-select" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}>
                    <option value="tbd">TBD</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                {formData.compensation === 'yes' && (
                  <div style={{ marginBottom: '0' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Amount</label>
                    <input type="text" name="compensation_amount" value={formData.compensation_amount} onChange={handleChange} placeholder="$100, $50/hour, etc." className="create-opportunity-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }} />
                  </div>
                )}
              </div>

              <div className="create-opportunity-description-section" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', boxShadow: 'var(--shadow-card)' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>Description *</label>
                <textarea name="description" value={formData.description} onChange={handleChange} required rows="15" placeholder="Describe the opportunity..." className="create-opportunity-textarea" style={{ width: '100%', padding: '16px', border: '1px solid var(--border-light)', borderRadius: '8px', fontSize: '15px', fontFamily: 'inherit', resize: 'vertical', lineHeight: '1.6' }} />
              </div>
            </div>

            <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>Contact Information</label>
              <textarea name="contact_info" value={formData.contact_info} onChange={handleChange} rows="3" placeholder="How should people reach you?" style={{ width: '100%', padding: '16px', border: '1px solid var(--border-light)', borderRadius: '8px', fontSize: '15px', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>

            <div style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '32px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-dark)' }}>Collaborators</h3>
                <button type="button" onClick={() => setShowAddCollab(!showAddCollab)} style={{ padding: '8px 16px', fontSize: '13px', background: 'var(--accent-primary)', color: 'var(--text-dark)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '700' }}>+ Add Collaborator</button>
              </div>

              {collaborators.length > 0 && (
                <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {collaborators.map((collab, i) => (
                    <div key={`collab-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--accent-primary)', color: 'var(--text-dark)', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                      <span>{collab.name}</span>
                      <button type="button" onClick={() => handleRemoveCollaborator(i)} style={{ background: 'none', border: 'none', color: 'var(--accent-danger)', cursor: 'pointer', fontSize: '16px', fontWeight: '700', padding: '0', lineHeight: '1' }} title="Remove">×</button>
                    </div>
                  ))}
                </div>
              )}

              {showAddCollab && (
                <div style={{ padding: '16px', background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: '8px' }}>
                  <input type="text" placeholder="Search for collaborator..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', marginBottom: '8px', fontSize: '14px' }} />
                  {searchResults.length > 0 && (
                    <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid var(--border-light)', borderRadius: '6px', background: 'white' }}>
                      {searchResults.map(result => (
                        <div key={result.id} onClick={() => handleAddCollaborator(result)} style={{ padding: '12px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)', color: 'var(--text-dark)', fontSize: '14px', transition: 'background 0.2s' }} onMouseEnter={(e) => { e.target.style.background = 'var(--smoke-gray)'; e.target.style.color = 'var(--accent-primary)'; }} onMouseLeave={(e) => { e.target.style.background = 'white'; e.target.style.color = 'var(--text-dark)'; }}>
                          <strong>{result.display_name}</strong>
                          <span style={{ marginLeft: '8px', fontSize: '13px', opacity: 0.7 }}>({result.city}, {result.state})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary" style={{ width: '100%', padding: '18px', fontSize: '18px', fontWeight: '700' }}>
              {loading ? 'Creating Opportunity...' : 'Create Opportunity'}
            </button>

          </form>
        </div>
      </div>
    </>
  );
};

export default CreateOpportunityPage;
